import { useMemo, useState } from 'react';
import {
  IInvoiceLogisticServiceRecord,
  IInvoiceOrderRecord,
  IInvoiceRecord,
  INVOICE_SECTION_VARIANT
} from '../InvoiceSectionOfBillingTab.types';
import { createInvoiceRecord, updateInvoiceRecord } from 'services/orders';
import { createLogisticServiceInvoiceRecord, updateLogisticServiceInvoiceRecord } from 'services/services';
import { updateStorageInvoiceRecord } from 'services/billing/storageBillingInvoices.service';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import InvoiceUploadButton from './InvoiceUploadButton/InvoiceUploadButton';
import InvoiceNumberAndEmissionDate from './InvoiceNumberAndEmissionDate/InvoiceNumberAndEmissionDate';
import useResetData from 'hooks/useResetData';
import { BILLING_PROVIDER_TYPE } from 'components/BillingDataTab/Components/BordInvoiceInfo/BordInvoiceInfo';
import './InvoiceRow.scss';

interface IInvoiceRow {
  invoiceRecord: IInvoiceOrderRecord | IInvoiceLogisticServiceRecord;
  handleUpdateCurrentInvoiceRecord: (invoiceRecord: IInvoiceOrderRecord | IInvoiceLogisticServiceRecord) => void;
  disabled?: boolean;
  customClassNameRow?: string;
  billingProvider?: string;
}

const InvoiceRow = ({
  invoiceRecord,
  handleUpdateCurrentInvoiceRecord,
  disabled,
  customClassNameRow = '',
  billingProvider
}: IInvoiceRow) => {
  const [isUpdatingOrCreatingInvoice, setIsUpdatingOrCreatingInvoice] = useState<boolean>(false);
  const { resetOrderDataWithLoaders, resetLSDataWithLoaders, resetStorageDataWithLoaders } = useResetData();
  const successMessage = useMemo(() => {
    return invoiceRecord?.type === INVOICE_SECTION_VARIANT.STORAGE
      ? 'La factura fue cargada con éxito'
      : 'La factura se actualizó correctamente';
  }, [invoiceRecord?.type]);
  const hasCredit = useMemo(() => {
    return billingProvider === BILLING_PROVIDER_TYPE.BORD;
  }, [billingProvider]);

  const createInvoice = async ({ invoiceNumber, invoiceEmissionDate, invoiceUrl }: IInvoiceRecord) => {
    if (invoiceRecord?.type === INVOICE_SECTION_VARIANT.ORDER) {
      const invoiceRecordToCreate = invoiceRecord as IInvoiceOrderRecord;
      return await createInvoiceRecord({
        invoiceNumber,
        invoiceEmissionDate,
        invoiceUrl,
        orderId: invoiceRecordToCreate?.orderId || undefined
      });
    } else if (invoiceRecord?.type === INVOICE_SECTION_VARIANT.LOGISTIC_SERVICE) {
      const invoiceRecordToCreate = invoiceRecord as IInvoiceLogisticServiceRecord;
      return await createLogisticServiceInvoiceRecord({
        invoiceNumber,
        invoiceEmissionDate,
        invoiceUrl,
        logisticServiceId: invoiceRecordToCreate?.logisticServiceId || undefined
      });
    }
  };

  const updateInvoiceByType = async (
    invoiceId: number,
    invoiceNumber: string | undefined,
    invoiceEmissionDate: string | undefined,
    invoiceUrl?: string | null
  ) => {
    const updateData = {
      invoiceNumber: invoiceNumber,
      invoiceEmissionDate: invoiceEmissionDate,
      invoiceUrl: invoiceUrl
    };

    switch (invoiceRecord?.type) {
      case INVOICE_SECTION_VARIANT.ORDER:
        await updateInvoiceRecord(invoiceId, updateData);
        break;
      case INVOICE_SECTION_VARIANT.LOGISTIC_SERVICE:
        await updateLogisticServiceInvoiceRecord(invoiceId, updateData);
        break;
      case INVOICE_SECTION_VARIANT.STORAGE:
        await updateStorageInvoiceRecord(invoiceId, updateData);
        break;
    }
  };

  const resetDataByInvoiceType = () => {
    switch (invoiceRecord?.type) {
      case INVOICE_SECTION_VARIANT.ORDER:
        resetOrderDataWithLoaders();
        break;
      case INVOICE_SECTION_VARIANT.LOGISTIC_SERVICE:
        resetLSDataWithLoaders();
        break;
      case INVOICE_SECTION_VARIANT.STORAGE:
        resetStorageDataWithLoaders();
        break;
    }
  };

  const updateOrCreateInvoice = async ({ invoiceNumber, invoiceEmissionDate, invoiceUrl }: IInvoiceRecord) => {
    if (!invoiceNumber && !invoiceEmissionDate && invoiceUrl === undefined) return;

    setIsUpdatingOrCreatingInvoice(true);
    try {
      if (!invoiceRecord?.id) {
        const createdInvoice = await createInvoice({ invoiceNumber, invoiceEmissionDate, invoiceUrl });
        handleUpdateCurrentInvoiceRecord(createdInvoice?.data || invoiceRecord);
        return;
      }

      await updateInvoiceByType(
        invoiceRecord.id,
        invoiceNumber ?? undefined,
        invoiceEmissionDate ?? undefined,
        invoiceUrl
      );

      displaySuccessNotification({
        customJSXMessage: <>{successMessage}</>,
        duration: 3000
      });
      resetDataByInvoiceType();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsUpdatingOrCreatingInvoice(false);
    }
  };

  return (
    <div className={`invoiceRow ${customClassNameRow}`}>
      <InvoiceNumberAndEmissionDate
        disabled={disabled}
        invoiceRecord={invoiceRecord}
        handleUpdateOrCreateInvoice={updateOrCreateInvoice}
        isUpdatingOrCreatingInvoice={isUpdatingOrCreatingInvoice}
      />
      <InvoiceUploadButton
        hasCredit={hasCredit}
        disabled={disabled}
        originalInvoiceUrl={invoiceRecord?.invoiceUrl || ''}
        handleUpdateOrCreateInvoice={updateOrCreateInvoice}
        isUpdatingOrCreatingInvoice={isUpdatingOrCreatingInvoice}
        variant={invoiceRecord?.type}
      />
    </div>
  );
};

export default InvoiceRow;
